/// <amd-module name="Wit/LinkedWorkItemsDropdown/Components/LinkedWorkItemsTable" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { Ago } from "VSSUI/Ago";
import { Button } from "VSSUI/Button";
import { IListBoxItem } from "VSSUI/ListBox";
import { ITableBreakpoint, ITableColumn, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { ago, AgoFormat } from "VSSUI/Utilities/Date";
import { VssPersona } from "VSSUI/VssPersona";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/LinkedWorkItemsDropdown/Resources";
import { WorkItemFormat } from "Wit/LinkedWorkItemsDropdown/Util/Data";
import { ILinkedWorkItemsTableProps } from "./ControlledLinkedWorkItemsDropdown.Props";
import { WorkItemIcon } from "./WorkItemIcon";
import { WorkItemState } from "./WorkItemState";

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [80, -250, 0, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [80, 200, 180, -100, 0]
    },
    {
        breakpoint: ScreenBreakpoints.medium,
        columnWidths: [80, 400, 270, 250, -100]
    }
];

function getCompactColumns(props: ILinkedWorkItemsTableProps): ITableColumn<IListBoxItem<WorkItem>>[] {
    const compactColumns = [
        {
            id: "main-column",
            width: -100,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <TwoLineTableCell
                        columnIndex={columnIndex}
                        key={columnIndex}
                        line1={
                            <>
                                <WorkItemIcon workItem={tableItem.data!} />
                                <FPSLink href={tableItem.href!} removeUnderline={true}>
                                    <Tooltip overflowOnly={true}>
                                        <span className="flex-grow text-ellipsis">
                                            {format(
                                                WorkItemFormat,
                                                tableItem.data!.fields[CoreFieldRefNames.WorkItemType],
                                                tableItem.id,
                                                tableItem.data!.fields[CoreFieldRefNames.Title]
                                            )}
                                        </span>
                                    </Tooltip>
                                </FPSLink>
                            </>
                        }
                        line2={
                            <span className="flex-row flex-center secondary-text">
                                <span className="padding-right-8">
                                    {format(Resources.LastUpdatedDateFormat, ago(tableItem.data!.fields[CoreFieldRefNames.ChangedDate]))}
                                </span>
                                <WorkItemState workItem={tableItem.data!} />
                            </span>
                        }
                    />
                );
            }
        }
    ];

    if (!props.readonly) {
        compactColumns.push({
            id: "delete-column",
            width: 56,
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex}>
                        <Button
                            ariaLabel={Resources.RemoveButtonText}
                            iconProps={{ iconName: "Cancel" }}
                            onClick={(event) => {
                                if (props.onRemoveItem) {
                                    props.onRemoveItem(tableItem);
                                    event.preventDefault();
                                }
                            }}
                            subtle={true}
                            tooltipProps={{ text: Resources.RemoveButtonText }}
                        ></Button>
                    </SimpleTableCell>
                );
            }
        });
    }

    return compactColumns;
}

function getExpandedColumns(props: ILinkedWorkItemsTableProps): ITableColumn<IListBoxItem<WorkItem>>[] {
    return [
        {
            id: "idColumn",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        {tableItem.id}
                    </SimpleTableCell>
                );
            },
            name: Resources.Id,
            width: new ObservableValue(80)
        },
        {
            id: "titleColumn",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <>
                            <WorkItemIcon workItem={tableItem.data!} />
                            <Tooltip overflowOnly={true}>
                                <span className="margin-left-4">{tableItem.data!.fields[CoreFieldRefNames.Title]}</span>
                            </Tooltip>
                        </>
                    </SimpleTableCell>
                );
            },
            name: Resources.Title,
            width: new ObservableValue(400)
        },
        {
            id: "updatedByColumn",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                const changedBy = tableItem.data!.fields[CoreFieldRefNames.ChangedBy];
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <>
                            <VssPersona
                                className="icon-margin"
                                displayName={changedBy.displayName}
                                imageUrl={changedBy.imageUrl}
                                size={"extra-small-plus"}
                            />
                            <Tooltip overflowOnly={true}>
                                <span className="text-ellipsis">{changedBy.displayName}</span>
                            </Tooltip>
                        </>
                    </SimpleTableCell>
                );
            },
            name: Resources.UpdatedBy,
            width: new ObservableValue(270)
        },
        {
            id: "dateColumn",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <Ago date={getDateInUserTimeZone(tableItem.data!.fields[CoreFieldRefNames.ChangedDate])} currentDate={getDateInUserTimeZone()} format={AgoFormat.Compact} tooltipTimeFormat={tooltipTimeFormat} />
                    </SimpleTableCell>
                );
            },
            name: Resources.UpdatedDate,
            width: new ObservableValue(250)
        },
        {
            id: "stateColumn",
            renderCell: (
                rowIndex: number,
                columnIndex: number,
                tableColumn: ITableColumn<IListBoxItem<WorkItem>>,
                tableItem: IListBoxItem<WorkItem>
            ) => {
                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <WorkItemState workItem={tableItem.data!} />
                    </SimpleTableCell>
                );
            },
            name: Resources.State,
            width: new ObservableValue(-100)
        }
    ];
}

export const LinkedWorkItemsTable: React.FunctionComponent<ILinkedWorkItemsTableProps> = (props) => {
    const { itemProvider, renderNoItemsContent } = props;

    if (itemProvider.length > 0) {
        if (!props.expanded) {
            return (
                <Table
                    className={props.className}
                    columns={getCompactColumns(props)}
                    excludeTabStop={true}
                    itemProvider={itemProvider}
                    onActivate={props.onActivate}
                    showHeader={false}
                    spacerWidth={2}
                />
            );
        } else {
            return (
                <Table
                    className={props.className}
                    columns={getExpandedColumns(props)}
                    itemProvider={itemProvider}
                    onActivate={props.onActivate}
                    tableBreakpoints={tableBreakpoints}
                />
            );
        }
    } else {
        return renderNoItemsContent ? renderNoItemsContent() : null;
    }
};
